import React, { useEffect, useState } from 'react'
import { Avatar, Box, Button, Chip, CircularProgress, Divider, LinearProgress, Stack, Tab, Tabs, Tooltip, Typography } from '@mui/material'
import { useGlobalStoreActions, useGlobalStoreData } from '../../store/globalStore'
import Content from '../../layout/content/Content'
import Head from '../../layout/head/Head'
import { useTranslation } from 'react-i18next'
import Grid from '@mui/material/Grid'
import '../../mailTemplate/src/styles/globals.scss'
import 'flag-icons/css/flag-icons.min.css'
import McPageHelpers from '../mc-components/McPageHelpers'
import { getBooleanUserRolesByUser } from '../../mailTemplate/src/utils/helpers'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined'
import UserActivities from '../../components/user/UserActivities'
import { useForm } from 'react-hook-form'

// Component Imports
import ProfileAutofillTabContent from '../../mailTemplate/src/components/atomic/organisms/ProfileAutofillTabContent'
import EmailSettingsTabContent from '../../mailTemplate/src/components/atomic/organisms/EmailSettingsTabContent'
import EmailSignaturesTabContent from '../../mailTemplate/src/components/atomic/organisms/EmailSignaturesTabContent'
import EmailSettingsTestButtons from '../../mailTemplate/src/components/atomic/molecules/EmailSettingsTestButtons'
import DeleteProfileConfirmDialog from '../../mailTemplate/src/components/atomic/molecules/DeleteProfileConfirmDialog'

// Service Imports
import { scrapeEmailGeneratorParameters, updateUser } from '../../mailTemplate/src/services/B2BServices'
import { getUserDetailsAndProcess } from '../../services/DataServiceMethods'

// Hook Imports
import { useToast } from '../../campaigns/hooks/useToast'
import { useProfileManagement } from '../../mailTemplate/src/hooks/useProfileManagement'
import { useEmailSettingsTests } from '../../mailTemplate/src/hooks/useEmailSettingsTests'

// Data Imports
import { countryEN } from '../../assets/data/country-en'
import { countryTR } from '../../assets/data/country-tr'
import { EMAIL_ACCOUNT_TYPES, LANGUAGES, TRADE_TYPES } from '../../mailTemplate/src/utils/campaignConstants'

// Utility Imports
import { createFormValuesFromProfile, extractEmailPurposeData, extractSenderCompanyData, filterEmptyValues } from '../../mailTemplate/src/utils/profileDataTransformers'

const ModernCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      backgroundColor: '#fff',
      borderRadius: '16px',
      boxShadow: '0 4px 24px rgba(0, 0, 0, 0.04)',
      padding: '2rem',
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.06)'
      },
      ...sx
    }}
  >
    {children}
  </Box>
)

const InfoRow = ({ icon: Icon, text }) => (
  <Stack direction="row" spacing={2} alignItems="center" sx={{ color: 'text.secondary', py: 1 }}>
    <Icon sx={{ fontSize: 20, color: 'text.secondary', opacity: 0.7 }}/>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>{text}</Typography>
  </Stack>
)

const UserProfile = () => {
  const { t } = useTranslation()
  const user = useGlobalStoreData.user()
  const userRoles = getBooleanUserRolesByUser(user)
  const countries = useGlobalStoreData.countries()

  const [limits, setLimits] = useState([])
  const [activeTab, setActiveTab] = useState(0)

  // Email Settings State
  const { showError, showSuccess } = useToast()
  const { setUserData } = useGlobalStoreActions()
  const languageGSD = useGlobalStoreData.language()

  const [loading, setLoading] = useState(false)
  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [accountType, setAccountType] = useState(
    user?.emailSettings?.accountType || EMAIL_ACCOUNT_TYPES.USERNAME_PASSWORD
  )

  const [profiles, setProfiles] = useState([])
  const [selectedProfileIndex, setSelectedProfileIndex] = useState(0)

  const [profileToDelete, setProfileToDelete] = useState(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  const [websiteUrl, setWebsiteUrl] = useState('')
  const [isFetchingFromWebsite, setIsFetchingFromWebsite] = useState(false)

  const {
    control,
    reset,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm()

  const watchedValues = watch()

  const getLocalizedCountryName = (countryName) => {
    if (!countryName) return ''
    const currentCountries = languageGSD === 'en' ? countryEN : countryTR
    const otherCountries = languageGSD === 'en' ? countryTR : countryEN

    const foundByCode = currentCountries.find(c => c.countryCode === countryName)
    if (foundByCode) return foundByCode.countryName

    const foundInCurrent = currentCountries.find(c => c.countryName === countryName)
    if (foundInCurrent) return countryName
    const foundInOther = otherCountries.find(c => c.countryName === countryName)
    if (foundInOther) {
      const translatedCountry = currentCountries.find(c => c.countryCode === foundInOther.countryCode)
      if (translatedCountry) return translatedCountry.countryName
    }
    return countryName
  }

  const {
    isAddingNewProfile,
    newProfileName,
    handleProfileChange,
    handleStartAddProfile,
    handleAddNewProfile,
    handleCancelAddProfile,
    handleProfileNameChange
  } = useProfileManagement(
    profiles,
    setProfiles,
    selectedProfileIndex,
    setSelectedProfileIndex,
    watch,
    reset,
    getLocalizedCountryName,
    user
  )

  const {
    testingSender,
    testingReceiver,
    isTestSenderDisabled,
    isTestReceiverDisabled,
    testSenderSettings,
    testReceiverSettings
  } = useEmailSettingsTests(
    accountType,
    user,
    watchedValues
  )

  useEffect(() => {
    if (user.limit) {
      setLimits(McPageHelpers.createLimitsArray(user.limit, userRoles.B2B))
    }

    const storeProfiles = user?.emailGeneratorParameters || []

    if (profiles.length === 0 || (profiles.length === 1 && profiles[0].name === 'default' && storeProfiles.length > 0)) {
      const initialProfiles = storeProfiles.length > 0
        ? storeProfiles
        : [
            {
              name: 'default',
              sender_company: user?.senderCompany || {
                name: '',
                industry: '',
                country: '',
                contact_person: '',
                contact_title: '',
                email: user?.emailSettings?.emailUserName || '',
                key_products: [],
                website: '',
                phone: '',
                production_capacity: '',
                facilities: [],
                certifications: [],
                market_experience: [],
                company_values: []
              },
              email_purpose: user?.emailPurpose || {
                target_market: '',
                trade_type: TRADE_TYPES.EXPORT,
                product_category: '',
                language: LANGUAGES.ENGLISH,
                specific_products: [],
                call_to_action: ''
              }
            }]

      setProfiles(initialProfiles)
      setSelectedProfileIndex(0)

      const formValues = createFormValuesFromProfile(
        initialProfiles[0],
        user?.emailSettings,
        getLocalizedCountryName
      )
      reset(formValues)

      setAccountType(
        user?.emailSettings?.accountType || EMAIL_ACCOUNT_TYPES.USERNAME_PASSWORD
      )
    }
  }, [user.id, user.limit, user?.emailGeneratorParameters?.length, userRoles.B2B, reset])

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue)
  }

  const handleSaveGeneratorSettings = (val) => {
    let updatedProfiles = [...profiles]

    if (isAddingNewProfile) {
      if (!newProfileName.trim()) {
        showError(t('mailTemplate.users.modal.profileNameRequired'))
        return
      }
      if (profiles.some(p => p.name === newProfileName.trim())) {
        showError(t('mailTemplate.users.modal.profileNameExists'))
        return
      }
      const senderCompanyData = extractSenderCompanyData(val)
      const emailPurposeData = extractEmailPurposeData(val)
      const filteredSenderCompany = filterEmptyValues(senderCompanyData)
      const filteredEmailPurpose = filterEmptyValues(emailPurposeData)

      const newProfile = {
        name: newProfileName.trim(),
        productName: val.productName?.map(item => item.value || item) || [],
        productListOp: val.productListOp || 'or',
        emailSignatureFileId: val.emailSignatureFileId || null,
        sender_company: Object.keys(filteredSenderCompany).length > 0 ? filteredSenderCompany : {},
        email_purpose: Object.keys(filteredEmailPurpose).length > 0 ? filteredEmailPurpose : {}
      }
      updatedProfiles = [...profiles, newProfile]
    } else {
      if (updatedProfiles[selectedProfileIndex]) {
        const senderCompanyData = extractSenderCompanyData(val)
        const emailPurposeData = extractEmailPurposeData(val)
        const filteredSenderCompany = filterEmptyValues(senderCompanyData)
        const filteredEmailPurpose = filterEmptyValues(emailPurposeData)

        updatedProfiles[selectedProfileIndex] = {
          ...updatedProfiles[selectedProfileIndex],
          productName: val.productName?.map(item => item.value || item) || [],
          productListOp: val.productListOp || 'or',
          emailSignatureFileId: val.emailSignatureFileId || null,
          sender_company: Object.keys(filteredSenderCompany).length > 0 ? filteredSenderCompany : {},
          email_purpose: Object.keys(filteredEmailPurpose).length > 0 ? filteredEmailPurpose : {}
        }
      }
    }

    setLoading(true)
    updateUser(user?.ownedB2bAccountId, { emailGeneratorParameters: updatedProfiles }).then(() => {
      getUserDetailsAndProcess(true, user, setUserData, true)
      setProfiles(updatedProfiles)
      showSuccess(t('mailTemplate.users.modal.toastSuccess'))
      if (isAddingNewProfile) {
        handleCancelAddProfile()
      }
    }).catch(() => {
      showError(t('mailTemplate.users.modal.toastErr'))
    }).finally(() => {
      setLoading(false)
    })
  }

  const handleSaveEmailLoginSettings = (val) => {
    const currentEmailSettings = {
      emailUserName: val.emailUserName,
      emailPassword: val.emailPassword,
      smtpHostName: val.smtpHostName,
      smtpPort: Number(val.smtpPort),
      transportStrategy: val.transportStrategy,
      rxHostName: val.rxHostName,
      rxPort: Number(val.rxPort),
      rxProtocol: val.rxProtocol,
      inboxFolderName: val.inboxFolderName
    }

    setLoading(true)
    updateUser(user?.ownedB2bAccountId, { emailSettings: currentEmailSettings }).then(() => {
      getUserDetailsAndProcess(true, user, setUserData, true)
      showSuccess(t('mailTemplate.users.modal.toastSuccess'))
    }).catch(() => {
      showError(t('mailTemplate.users.modal.toastErr'))
    }).finally(() => {
      setLoading(false)
    })
  }

  const handleDeleteProfile = (index) => {
    if (profiles.length <= 1) {
      showError(t('mailTemplate.users.modal.cannotDeleteLastProfile'))
      return
    }
    setProfileToDelete(index)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (profileToDelete === null) return
    const updatedProfiles = profiles.filter((_, i) => i !== profileToDelete)
    setLoading(true)
    updateUser(user?.ownedB2bAccountId, { emailGeneratorParameters: updatedProfiles }).then(() => {
      getUserDetailsAndProcess(true, user, setUserData, true)
      showSuccess(t('mailTemplate.users.modal.profileDeleted'))
      setProfiles(updatedProfiles)
      if (selectedProfileIndex >= updatedProfiles.length) {
        handleProfileChange(updatedProfiles.length - 1)
      } else if (selectedProfileIndex === profileToDelete) {
        handleProfileChange(0)
      }
    }).catch(() => {
      showError(t('mailTemplate.users.modal.profileDeleteFailed'))
    }).finally(() => {
      setLoading(false)
      setDeleteConfirmOpen(false)
      setProfileToDelete(null)
    })
  }

  const handleFetchFromWebsite = async () => {
    if (!websiteUrl.trim()) {
      showError(t('mailTemplate.users.modal.websiteUrlRequired') || 'Please enter a website URL')
      return
    }

    setIsFetchingFromWebsite(true)
    try {
      const response = await scrapeEmailGeneratorParameters(websiteUrl.trim())
      const scrapedData = response.data
      if (scrapedData) {
        const localizedCountry = scrapedData.sender_company?.country
          ? getLocalizedCountryName(scrapedData.sender_company.country)
          : watchedValues['senderCompany.country']

        const localizedTargetMarket = scrapedData.email_purpose?.target_market
          ? getLocalizedCountryName(scrapedData.email_purpose.target_market)
          : watchedValues['emailPurpose.target_market']

        const updatedSenderCompany = {
          ...watchedValues.senderCompany,
          name: scrapedData.sender_company?.name ?? watchedValues['senderCompany.name'],
          industry: scrapedData.sender_company?.industry ?? watchedValues['senderCompany.industry'],
          country: localizedCountry,
          contact_person: scrapedData.sender_company?.contact_person ?? watchedValues['senderCompany.contact_person'],
          contact_title: scrapedData.sender_company?.contact_title ?? watchedValues['senderCompany.contact_title'],
          email: scrapedData.sender_company?.email ?? watchedValues['senderCompany.email'],
          key_products: scrapedData.sender_company?.key_products ?? watchedValues['senderCompany.key_products'],
          website: scrapedData.sender_company?.website ?? watchedValues['senderCompany.website'],
          phone: scrapedData.sender_company?.phone ?? watchedValues['senderCompany.phone'],
          production_capacity: scrapedData.sender_company?.production_capacity ?? watchedValues['senderCompany.production_capacity'],
          facilities: scrapedData.sender_company?.facilities ?? watchedValues['senderCompany.facilities'],
          certifications: scrapedData.sender_company?.certifications ?? watchedValues['senderCompany.certifications'],
          market_experience: scrapedData.sender_company?.market_experience ?? watchedValues['senderCompany.market_experience'],
          company_values: scrapedData.sender_company?.company_values ?? watchedValues['senderCompany.company_values']
        }

        const updatedEmailPurpose = {
          ...watchedValues.emailPurpose,
          target_market: localizedTargetMarket,
          trade_type: scrapedData.email_purpose?.trade_type ?? watchedValues['emailPurpose.trade_type'],
          product_category: scrapedData.email_purpose?.product_category ?? watchedValues['emailPurpose.product_category'],
          language: scrapedData.email_purpose?.language ?? watchedValues['emailPurpose.language'],
          specific_products: scrapedData.email_purpose?.specific_products ?? watchedValues['emailPurpose.specific_products'],
          call_to_action: scrapedData.email_purpose?.call_to_action ?? watchedValues['emailPurpose.call_to_action']
        }

        const updatedValues = {
          ...watchedValues,
          'senderCompany.name': updatedSenderCompany.name,
          'senderCompany.industry': updatedSenderCompany.industry,
          'senderCompany.country': updatedSenderCompany.country,
          'senderCompany.contact_person': updatedSenderCompany.contact_person,
          'senderCompany.contact_title': updatedSenderCompany.contact_title,
          'senderCompany.email': updatedSenderCompany.email,
          'senderCompany.key_products': updatedSenderCompany.key_products,
          'senderCompany.website': updatedSenderCompany.website,
          'senderCompany.phone': updatedSenderCompany.phone,
          'senderCompany.production_capacity': updatedSenderCompany.production_capacity,
          'senderCompany.facilities': updatedSenderCompany.facilities,
          'senderCompany.certifications': updatedSenderCompany.certifications,
          'senderCompany.market_experience': updatedSenderCompany.market_experience,
          'senderCompany.company_values': updatedSenderCompany.company_values,
          'emailPurpose.target_market': updatedEmailPurpose.target_market,
          'emailPurpose.trade_type': updatedEmailPurpose.trade_type,
          'emailPurpose.product_category': updatedEmailPurpose.product_category,
          'emailPurpose.language': updatedEmailPurpose.language,
          'emailPurpose.specific_products': updatedEmailPurpose.specific_products,
          'emailPurpose.call_to_action': updatedEmailPurpose.call_to_action,
          senderCompany: updatedSenderCompany,
          emailPurpose: updatedEmailPurpose
        }
        reset(updatedValues)
        showSuccess(t('mailTemplate.users.modal.dataFetchedSuccessfully'))
      }
    } catch (error) {
      showError(t('mailTemplate.users.modal.dataFetchFailed'))
    } finally {
      setIsFetchingFromWebsite(false)
    }
  }

  const getUserCountryName = () => {
    return countries.find((item) => item.countryCode === user.countryCode)?.countryName || t('unknown')
  }

  const getLocationString = () => {
    const city = user.city || ''
    const country = getUserCountryName() || ''
    return [city, country].filter(Boolean).join(', ')
  }

  return (
    <React.Fragment>
      <Head title={t('menu.user')}/>
      <Content>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4} lg={3}>
            <ModernCard sx={{ textAlign: 'center' }}>
              <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                <Avatar
                  alt={user.nameSurname || user.userName}
                  src={user.avatar || '/default-avatar.png'}
                  sx={{
                    width: 120,
                    height: 120,
                    boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
                    border: '4px solid #fff',
                    margin: '0 auto'
                  }}
                />
              </Box>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1a1d21' }}>
                {user.nameSurname || user.userName}
              </Typography>

              <Box sx={{ mb: 3 }}>
                {user.roles?.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    sx={{
                      borderRadius: '8px',
                      bgcolor: 'rgba(0,0,0,0.05)',
                      fontWeight: 600,
                      color: 'text.secondary'
                    }}
                  />
                ))}
              </Box>

              <Divider sx={{ my: 3, opacity: 0.6 }}/>

              <Stack spacing={1} sx={{ textAlign: 'left', px: 1 }}>
                {user.companyName && <InfoRow icon={BusinessOutlinedIcon} text={user.companyName}/>}
                <InfoRow icon={LocationOnOutlinedIcon} text={getLocationString()}/>
                <InfoRow icon={EmailOutlinedIcon} text={user.email}/>
                <InfoRow icon={PhoneOutlinedIcon} text={user.tel}/>

                {user.openDate && (
                  <InfoRow
                    icon={CalendarTodayOutlinedIcon}
                    text={`${t('mailTemplate.users.openDate')}: ${new Date(user.openDate).toLocaleDateString()}`}
                  />
                )}
              </Stack>

              {user?.roles && (
                <Box sx={{ mt: 3, textAlign: 'left', px: 1 }}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 1, fontWeight: 600, letterSpacing: '0.5px' }}>
                    {t('limit.allowedCountries').toUpperCase()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {(McPageHelpers.getAllowedSearchCountries(user) || []).map(country => countries.find(item => item.countryCode === country)?.countryName || t('unknown')).join(', ')}
                  </Typography>
                </Box>
              )}
            </ModernCard>
          </Grid>

          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={activeTab} onChange={handleTabChange} aria-label="user profile tabs">
                <Tab label={t('common.limits') || 'Limits'}/>
                <Tab label={t('common.activities') || 'Activities'} disabled/>
                <Tab label={t('mailTemplate.wizard.ai.emailGeneratorSettings')}/>
                <Tab label={t('signIn.mailInfo')}/>
                <Tab label={t('mailTemplate.users.emailSignaturesTab') || 'Email Signatures'}/>
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <Grid container spacing={3}>
                {limits.map((limit, index) => (
                  <Grid item xs={12} sm={6} lg={6} xl={4} key={limit.key}>
                    <ModernCard sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ p: 3, flexGrow: 1 }}>
                        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '0.9rem', color: '#1a1d21' }}>
                              {t(limit.label)}
                            </Typography>
                            {!limit.isInfinite && limit.key !== 'b2bSearchedCountrySet' && (
                              <Chip
                                label={limit.isInfinite ? '∞' : `${Math.round((limit.used / limit.total) * 100) || 0}%`}
                                size="small"
                                color={!limit.isInfinite && limit.used / limit.total > 0.9 ? 'error' : 'primary'}
                                variant="outlined"
                                sx={{
                                  borderRadius: '6px',
                                  fontWeight: 700,
                                  border: 'none',
                                  bgcolor: !limit.isInfinite && limit.used / limit.total > 0.9 ? 'rgba(211, 47, 47, 0.1)' : 'rgba(25, 118, 210, 0.1)'
                                }}
                              />
                            )}
                          </Box>

                          <Box sx={{ mb: 2 }}>
                            {limit.key !== 'b2bSearchedCountrySet' && (
                              <LinearProgress
                                variant="determinate"
                                value={limit.isInfinite ? 100 : Math.min(((limit.used / limit.total) * 100), 100) || 0}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: 'rgba(0,0,0,0.05)',
                                  '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    background: limit.isInfinite
                                      ? 'linear-gradient(90deg, #00C853 0%, #B2FF59 100%)'
                                      : limit.used / limit.total > 0.9
                                        ? 'linear-gradient(90deg, #FF6B6B 0%, #EE5253 100%)'
                                        : 'linear-gradient(90deg, #4facfe 0%, #00f2fe 100%)'
                                  }
                                }}
                              />
                            )}
                          </Box>

                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 'auto' }}>
                            {(limit.key !== 'b2bSearchedCountrySet' || (limit.used && limit.used > 0)) && (
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                                  {t('limit.used')}
                                </Typography>
                                {limit.isSet
                                  ? (
                                    <Tooltip
                                      title={
                                        <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                          {limit.usedItems?.map((item, i) => (
                                            <Typography key={i} variant="caption" display="block">
                                              {item}
                                            </Typography>
                                          ))}
                                        </Box>
                                      }
                                      arrow
                                    >
                                      <Typography variant="body1" sx={{ fontWeight: 600, cursor: 'help', textDecoration: 'underline dotted' }}>
                                        {limit.used}
                                      </Typography>
                                    </Tooltip>
                                    )
                                  : (
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      {limit.used}
                                    </Typography>
                                    )}
                              </Box>
                            )}
                            {(limit.key !== 'b2bSearchedCountrySet' || (limit.used && limit.used > 0)) && <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center' }}/>}
                            <Box>
                              <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                                {
                                  t(limit.key === 'b2bSearchedCountrySet'
                                    ? 'limit.b2bDefinedCountryList'
                                    : limit.key === 'b2bHsCodeSet'
                                      ? 'limit.b2bDefinedHsCodeList'
                                      : 'limit.total')
                                }
                              </Typography>
                              {limit.isInfinite
                                ? (
                                  <Typography variant="body1" sx={{ fontWeight: 600, fontSize: limit.isSet ? '1rem' : '1.2rem', lineHeight: 1 }}>
                                    {limit.isSet ? t('filter_all') : '∞'}
                                  </Typography>
                                  )
                                : (
                                    limit.isSet
                                      ? (
                                      <Tooltip
                                        title={
                                          <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                                            {limit.totalItems?.map((item, i) => (
                                              <Typography key={i} variant="caption" display="block">
                                                {limit.key === 'b2bSearchedCountrySet' ? getLocalizedCountryName(item) : item}
                                              </Typography>
                                            ))}
                                          </Box>
                                        }
                                        arrow
                                      >
                                        <Typography variant="body1" sx={{ fontWeight: 600, cursor: 'help', textDecoration: 'underline dotted' }}>
                                          {limit.total}
                                        </Typography>
                                      </Tooltip>
                                        )
                                      : (
                                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {limit.total}
                                      </Typography>
                                        )
                                  )}
                            </Box>
                            {limit.key !== 'b2bSearchedCountrySet' && <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center' }}/>}
                            {limit.key !== 'b2bSearchedCountrySet' && (
                              <Box>
                                <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block' }}>
                                  {t('limit.balance')}
                                </Typography>
                                {limit.isInfinite
                                  ? (
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main', fontSize: '1.2rem', lineHeight: 1 }}>
                                      ∞
                                    </Typography>
                                    )
                                  : (
                                    <Typography variant="body1" sx={{ fontWeight: 600, color: limit.total - limit.used < 10 ? 'error.main' : 'success.main' }}>
                                      {Math.max(0, limit.total - limit.used)}
                                    </Typography>
                                    )}
                              </Box>
                            )}
                          </Stack>
                        </Box>
                      </Box>
                    </ModernCard>
                  </Grid>
                ))}
              </Grid>
            )}

            {activeTab === 1 && (
              <ModernCard>
                <UserActivities userName={user.userName}/>
              </ModernCard>
            )}

            {activeTab === 2 && (
              <ModernCard>
                <ProfileAutofillTabContent
                  control={control}
                  companyValidationRules={{}}
                  profiles={profiles}
                  selectedProfileIndex={selectedProfileIndex}
                  isAddingNewProfile={isAddingNewProfile}
                  onProfileChange={handleProfileChange}
                  onAddProfile={handleAddNewProfile}
                  onDeleteProfile={handleDeleteProfile}
                  onCancelAdd={handleCancelAddProfile}
                  onStartAdd={handleStartAddProfile}
                  onProfileNameChange={handleProfileNameChange}
                  websiteUrl={websiteUrl}
                  onWebsiteUrlChange={setWebsiteUrl}
                  onFetchFromWebsite={handleFetchFromWebsite}
                  isFetchingFromWebsite={isFetchingFromWebsite}
                />
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    onClick={handleSubmit(handleSaveGeneratorSettings)}
                    disabled={loading || (isAddingNewProfile && !newProfileName.trim())}
                    startIcon={loading ? <CircularProgress size={20} color="inherit"/> : null}
                  >
                    {t('mailTemplate.wizard.rte.saveBtn')}
                  </Button>
                </Box>
              </ModernCard>
            )}

            {activeTab === 3 && (
              <ModernCard>
                <EmailSettingsTabContent
                  control={control}
                  errors={errors}
                  accountType={accountType}
                  isPasswordShown={isPasswordShown}
                  languageGSD={languageGSD}
                  userEditState={user}
                  onAccountTypeChange={(e) => setAccountType(e.target.value)}
                  onTogglePassword={() => setIsPasswordShown(!isPasswordShown)}
                />
                {accountType !== EMAIL_ACCOUNT_TYPES.EXTERNAL_SERVICE && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {accountType === EMAIL_ACCOUNT_TYPES.USERNAME_PASSWORD
                      ? (
                        <EmailSettingsTestButtons
                          testingSender={testingSender}
                          testingReceiver={testingReceiver}
                          isTestSenderDisabled={isTestSenderDisabled}
                          isTestReceiverDisabled={isTestReceiverDisabled}
                          onTestSender={handleSubmit(testSenderSettings)}
                          onTestReceiver={handleSubmit(testReceiverSettings)}
                        />
                        )
                      : <Box/>}
                    <Button
                      variant="contained"
                      onClick={handleSubmit(handleSaveEmailLoginSettings)}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} color="inherit"/> : null}
                    >
                      {t('mailTemplate.wizard.rte.saveBtn')}
                    </Button>
                  </Box>
                )}
              </ModernCard>
            )}

            {activeTab === 4 && (
              <ModernCard>
                <EmailSignaturesTabContent/>
              </ModernCard>
            )}
          </Grid>
        </Grid>
      </Content>
      <DeleteProfileConfirmDialog
        open={deleteConfirmOpen}
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setDeleteConfirmOpen(false)
          setProfileToDelete(null)
        }}
        profileName={profileToDelete !== null ? profiles[profileToDelete]?.name : ''}
      />
    </React.Fragment>
  )
}

export default UserProfile
